import { FC, ReactElement, useCallback, useMemo } from 'react';

import { useResolvedAdElements } from '@adflow/ad-resolution';
import type { AdElement } from '@adflow/types';
import { Box, Button, ButtonGroup, Stack } from '@chakra-ui/react';

import { useEditorStageView } from '../../contexts/EditorStageViewContext';
import { useStore } from '../../hooks/useStore';
import { EditorCanvas } from '../EditorCanvas/EditorCanvas';
import type { RenderPreviewArgs } from '../Editor';
import { resolveSoccerCustomAssetUrlsForEditor } from './resolveSoccerCustomAssetUrlsForEditor';

export type EditorCenterStageWithSharedResolutionProps = {
  renderPreview: (args: RenderPreviewArgs) => ReactElement;
  previewArgs: RenderPreviewArgs;
};

export const EditorCenterStageWithSharedResolution: FC<
  EditorCenterStageWithSharedResolutionProps
> = ({ renderPreview, previewArgs }) => {
  const { stageView, setStageView } = useEditorStageView();

  const { addElements, setElementSelected } = useStore((state) => ({
    addElements: state.addElements,
    setElementSelected: state.setElementSelected,
  }));

  const resolveSoccerCustomAssetUrls = useMemo(
    () => resolveSoccerCustomAssetUrlsForEditor(previewArgs.dataSources),
    [previewArgs.dataSources],
  );

  const { resolvedElements, animationOverrides } = useResolvedAdElements({
    elements: previewArgs.elements,
    brand: previewArgs.brand,
    brandKit: previewArgs.brandKit,
    fonts: previewArgs.fonts,
    displayOptions: previewArgs.displayOptions,
    dataSources: previewArgs.dataSources,
    animation: previewArgs.animation,
    animationPlaybackToken: previewArgs.animationPlaybackToken,
    loadFonts: previewArgs.fonts.length > 0,
    resolveSoccerCustomAssetUrls,
  });

  const onElementsChange = useCallback(
    (next: AdElement[]) => {
      addElements(next);
    },
    [addElements],
  );

  const onSelectElement = useCallback(
    (elementId: string | null) => {
      setElementSelected(elementId);
    },
    [setElementSelected],
  );

  return (
    <Stack
      spacing={3}
      align="stretch"
      flex={1}
      mt={3}
      minH={0}
      width="100%"
      height="100%"
      data-test-id="editor-canvas-feature-state"
      data-canvas-enabled="true"
    >
      <Box alignSelf="center">
        <ButtonGroup
          size="sm"
          isAttached
          variant="outline"
          data-test-id="template-builder-stage-view-toggle"
        >
          <Button
            isActive={stageView === 'preview'}
            aria-pressed={stageView === 'preview'}
            onClick={() => setStageView('preview')}
            data-test-id="template-builder-stage-preview"
          >
            Preview
          </Button>
          <Button
            isActive={stageView === 'editor'}
            aria-pressed={stageView === 'editor'}
            onClick={() => setStageView('editor')}
            data-test-id="template-builder-stage-editor"
          >
            Editor
          </Button>
        </ButtonGroup>
      </Box>

      <Box
        flex={1}
        minH={0}
        width="100%"
        display="flex"
        flexDirection="column"
        alignItems={stageView === 'preview' ? 'center' : 'stretch'}
        justifyContent={stageView === 'preview' ? 'center' : 'flex-start'}
        overflow={stageView === 'preview' ? 'auto' : 'hidden'}
        data-test-id="editor-center-stage-body"
      >
        {stageView === 'editor' ? (
          <EditorCanvas
            elements={previewArgs.elements}
            resolvedElements={resolvedElements}
            animationOverrides={animationOverrides}
            animation={previewArgs.animation ?? null}
            selectedElementId={previewArgs.selectedElementId}
            onSelectElement={onSelectElement}
            onElementsChange={onElementsChange}
            fillContainer
          />
        ) : (
          renderPreview(previewArgs)
        )}
      </Box>
    </Stack>
  );
};

EditorCenterStageWithSharedResolution.displayName = 'EditorCenterStageWithSharedResolution';
